import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import AppShell from "./components/AppShell";
import { BrandingHead } from "./components/BrandingHead";
import { LoginPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";
import AssetsPage from "./pages/AssetsPage";
import DashboardBuilder from "./pages/DashboardBuilder";
import ImportantLinksPage from "./pages/ImportantLinksPage";
import OrgChartPage from "./components/OrgChart";
import Agency from "./pages/Agency";
import Dashboard from "./pages/Dashboard";
import Contacts from "./pages/Contacts";
import Pipelines from "./pages/Pipelines";
import Conversations from "./pages/Conversations";
import CalendarPage from "./pages/CalendarPage";
import Forms from "./pages/Forms";
import Workflows from "./pages/Workflows";
import Funnels from "./pages/Funnels";
import RecordCentre from "./pages/RecordCentre";
import Counsellor from "./pages/Counsellor";
import Integrations from "./pages/Integrations";
import Compliance from "./pages/Compliance";
import AIAssistant from "./pages/AIAssistant";
import SettingsPage from "./pages/SettingsPage";
import PublicForm from "./pages/PublicForm";
import SecureDoc from "./pages/SecureDoc";
import PublicSite from "./pages/PublicSite";
import HostSite from "./pages/HostSite";
import { CUSTOM_HOST } from "./lib/api";
import BosConcept, { BosLegal } from "./pages/BosConcept";
import Websites from "./pages/Websites";
import FormFill from "./pages/FormFill";
import FormPreview from "./pages/FormPreview";
import DocumentEssentials from "./pages/DocumentEssentials";
import MissionPage from "./pages/MissionPage";
import PresenceOverview, { PresenceSite } from "./pages/Presence";
import PublicFunnel from "./pages/PublicFunnel";

function Protected({ children, owner }) {
  const { user, accountId } = useAuth();
  if (user === null) return <div className="min-h-screen bg-black" />;
  if (!user) return <Navigate to="/login" replace />;
  if (owner && user.role !== "agency_owner") return <Navigate to="/app/dashboard" replace />;
  if (!owner && !accountId) return <Navigate to="/agency" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "agency_owner" ? "/agency" : "/app/dashboard"} replace />;
}

export default function App() {
  if (CUSTOM_HOST) return <HostSite />;
  return (
    <AuthProvider>
      <BrandingHead />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/f/:id" element={<PublicForm />} />
          <Route path="/form-preview" element={<FormPreview />} />
          <Route path="/secure-doc/:token" element={<SecureDoc />} />
          <Route path="/site/:slug" element={<PublicSite />} />
          <Route path="/site/:slug/:page" element={<PublicSite />} />
          <Route path="/concept/bos-hq" element={<BosConcept />} />
          <Route path="/concept/privacy" element={<BosLegal kind="privacy" />} />
          <Route path="/concept/terms" element={<BosLegal kind="terms" />} />
          <Route path="/p/:slug" element={<PublicFunnel />} />
          <Route path="/agency" element={<Protected owner><AppShell mode="agency" /></Protected>}>
            <Route index element={<Agency />} />
            <Route path=":tab" element={<Agency />} />
          </Route>
          <Route path="/app" element={<Protected><AppShell /></Protected>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="pipelines" element={<Pipelines />} />
            <Route path="conversations" element={<Conversations />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="forms" element={<Forms />} />
            <Route path="forms/fill/:id" element={<FormFill />} />
            <Route path="essentials" element={<DocumentEssentials />} />
            <Route path="workflows" element={<Workflows />} />
            <Route path="funnels" element={<Funnels />} />
            <Route path="websites" element={<Websites />} />
            <Route path="record-centre" element={<RecordCentre />} />
            <Route path="counsellor" element={<Counsellor />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="ai" element={<AIAssistant />} />
            <Route path="assets" element={<AssetsPage />} />
            <Route path="links" element={<ImportantLinksPage />} />
            <Route path="org-chart" element={<OrgChartPage />} />
            <Route path="mission" element={<MissionPage />} />
            <Route path="presence" element={<PresenceOverview />} />
            <Route path="presence/site/:sid" element={<PresenceSite />} />
            <Route path="dashboards" element={<DashboardBuilder />} />
            <Route path="widgets" element={<Navigate to="/app/dashboards" replace />} />
            <Route path="contact-import" element={<Navigate to="/app/settings" replace />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </AuthProvider>
  );
}
